import React from "react";
import { CheckCircle2, AlertTriangle, Info, ArrowRight } from "lucide-react";
import {
  worldCountryByCode, SCHENGEN_CODES, VISA_FREE_CODES, SCHENGEN_ADJECTIVE, demonymForCode,
} from "@/lib/worldCountries";
import { Flag } from "@/components/Flag";

/**
 * Eligibility banner shown based on the nationality category:
 *  1. Schengen national      → orange warning, blocks the rest of the form
 *  2. Visa-free national     → blue info banner with "I still need travel documents" CTA;
 *                              parent decides whether to allow continuing
 *  3. Visa-required national → green eligible banner with applying-from + destination details
 *
 * Props (all ISO-2 codes / Schengen country name / applying-from city string):
 *   nationalityCode      e.g. "PK"
 *   applyingFromCode     e.g. "SA"
 *   applyingFromCity     e.g. "Riyadh" (optional, included in eligible banner if present)
 *   destinationName      Schengen country name, e.g. "France"
 *   visaFreeAcknowledged when true and category=visa-free, suppress the blue banner and show
 *                       the green banner once the other fields are filled.
 *   onVisaFreeAcknowledge() called when user clicks "I still need travel documents → Continue"
 */
export default function EligibilityBanner({
  nationalityCode, applyingFromCode, applyingFromCity = "", destinationName,
  visaFreeAcknowledged = false, onVisaFreeAcknowledge,
  testId = "eligibility-banner",
}) {
  if (!nationalityCode) return null;

  const natCountry = worldCountryByCode(nationalityCode);
  const demonym = demonymForCode(nationalityCode) || (natCountry?.name || "");

  // ----- Category 1: Schengen national -----
  if (SCHENGEN_CODES.has(nationalityCode)) {
    return (
      <div
        data-testid={`${testId}-schengen-warning`}
        className="mt-3 flex items-start gap-2.5 rounded-md border border-[#fbbf24] bg-[#fffbeb] px-3 py-2.5 text-sm"
      >
        <AlertTriangle className="w-4 h-4 text-[#b45309] flex-shrink-0 mt-0.5" />
        <span className="text-[#78350f] leading-snug">
          <span className="font-semibold">{demonym} nationals</span> are already part of the Schengen Area and do not need a Schengen visa.
        </span>
      </div>
    );
  }

  // ----- Category 2: Visa-free national -----
  if (VISA_FREE_CODES.has(nationalityCode) && !visaFreeAcknowledged) {
    return (
      <div
        data-testid={`${testId}-visa-free`}
        className="mt-3 rounded-md border-l-2 border-[#2563eb] bg-[#eff6ff] px-3 py-3 text-sm"
      >
        <div className="flex items-start gap-2.5">
          <Info className="w-4 h-4 text-[#2563eb] flex-shrink-0 mt-0.5" />
          <div className="leading-relaxed text-[#1e3a8a] min-w-0">
            <p>
              <span className="font-semibold">{demonym} nationals</span> can enter the Schengen Area visa-free for up to 90 days within any 180-day period.
            </p>
            <p className="mt-2">
              You do not need a visa for short tourism or business trips. However, if you need supporting documents such as a flight reservation or hotel booking for border control or travel insurance purposes, we can still help you.
            </p>
          </div>
        </div>
        {onVisaFreeAcknowledge && (
          <div className="mt-3 pl-6">
            <button
              type="button"
              onClick={onVisaFreeAcknowledge}
              data-testid={`${testId}-visa-free-continue`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2563eb] hover:text-[#1d4ed8] underline underline-offset-2"
            >
              I still need travel documents <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    );
  }

  // ----- Category 3: Visa-required national (green eligible) -----
  // Only render when all three location fields are set.
  if (!applyingFromCode || !destinationName) return null;
  const applying = worldCountryByCode(applyingFromCode);
  const destAdj = SCHENGEN_ADJECTIVE[destinationName] || destinationName;
  const cityPart = applyingFromCity.trim();
  const consulateLocation = cityPart && applying?.name
    ? `${cityPart}, ${applying.name}`
    : (applying?.name || cityPart || "");

  return (
    <div
      data-testid={`${testId}-eligible`}
      className="mt-3 flex items-start gap-2.5 rounded-md border border-[#bbf7d0] bg-[#f0fdf4] px-3 py-2.5 text-sm"
    >
      <CheckCircle2 className="w-4 h-4 text-[#16a34a] flex-shrink-0 mt-0.5" />
      <div className="leading-snug text-[#14532d]">
        <div className="font-semibold inline-flex items-center flex-wrap gap-1.5">
          <Flag code={natCountry?.code} />
          <span>{demonym} national</span>
          <span className="mx-1.5 text-[#65a30d]">·</span>
          <span>Applying from</span>
          <Flag code={applying?.code} />
          <span>{applying?.name || ""}</span>
        </div>
        <div className="mt-0.5 text-[12px] text-[#166534]">
          Eligible for document preparation · Apply at the <span className="font-semibold">{destAdj} Consulate</span> in {consulateLocation}
        </div>
      </div>
    </div>
  );
}
